import { Injectable } from '@angular/core';
import { DateTimeService } from '@examdojo/core/date-time';
import { debugObservable } from '@examdojo/core/rxjs';
import { QueryEntity } from '@examdojo/state';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DailyHoursMap } from '../payments.model';
import { PayoutRateQuery, PayoutRateUIModel } from '../payout-rates';
import {
  PAYOUT_REQUEST_STATUS_LABELS,
  PayoutRequestStatus,
  PayoutRequestStoreModel,
  PayoutRequestUIModel,
} from './payout-request.model';
import { PayoutRequestState, PayoutRequestStore } from './payout-request.store';

@Injectable({
  providedIn: 'root',
})
export class PayoutRequestQuery extends QueryEntity<
  PayoutRequestState,
  PayoutRequestStoreModel,
  PayoutRequestUIModel,
  'id'
> {
  constructor(
    protected override readonly store: PayoutRequestStore,
    private readonly dateTimeService: DateTimeService,
    private readonly payoutRateQuery: PayoutRateQuery,
  ) {
    super(
      store,
      () => [this.payoutRateQuery.selectAllEntities()],
      () => [],
      (entity: PayoutRequestStoreModel) => this.mapToUIModel(entity), // UI mapping function
    );
  }

  selectPayoutRequests(): Observable<PayoutRequestUIModel[]> {
    return this.entities$;
  }

  selectCurrentPeriodRequest(): Observable<PayoutRequestUIModel | null> {
    const currentPeriod = this.getCurrentPeriod();
    return this.entities$.pipe(
      map((requests: PayoutRequestUIModel[]) => requests.find((request) => request.period === currentPeriod) || null),
    );
  }

  selectHistoricalRequests(): Observable<PayoutRequestUIModel[]> {
    const currentPeriod = this.getCurrentPeriod();
    return this.entities$.pipe(
      map((requests: PayoutRequestUIModel[]) =>
        requests
          .filter((request) => request.period !== currentPeriod)
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
      ),
    );
  }

  override selectLoading(): Observable<boolean> {
    return super.selectLoading().pipe(debugObservable('selectLoading'));
  }

  getCurrentPeriodRequest(): PayoutRequestUIModel | null {
    const currentPeriod = this.getCurrentPeriod();
    return this.getAllUIEntities().find((request) => request.period === currentPeriod) || null;
  }

  getHistoricalRequests(): PayoutRequestUIModel[] {
    const currentPeriod = this.getCurrentPeriod();
    return this.getAllUIEntities()
      .filter((request) => request.period !== currentPeriod)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  private mapToUIModel(request: PayoutRequestStoreModel): PayoutRequestUIModel {
    const totalHours = this.calculateTotalHours(request.hours);
    const currentRate = this.payoutRateQuery.getUserRate(request.user_id);

    let appliedRate: PayoutRateUIModel | null = null;

    if (request.payout_rate_id) {
      appliedRate = this.payoutRateQuery.getEntity(request.payout_rate_id);
    } else {
      const [year, month] = request.period.split('-');
      const periodStart = new Date(`${year}-${month}-01T00:00:00Z`);
      const periodEnd = new Date(Date.UTC(parseInt(year), parseInt(month), 0, 23, 59, 59, 999));

      const userRates = this.payoutRateQuery.getUserRates(request.user_id);
      appliedRate = this.findNewestRateForPeriod(userRates, periodStart, periodEnd);
    }

    const appliedHourlyRate = appliedRate?.rate ?? 0;
    const estimatedPayout = totalHours * appliedHourlyRate;

    const calculatedFinalPayout =
      request.status === 'DRAFT' ? estimatedPayout + (request.extra_bonus_in_eur ?? 0) : request.final_payout_in_eur;

    return {
      ...request,
      current_hourly_rate: currentRate?.rate ?? 0,
      isEditable: request.status === 'DRAFT',
      canSubmit: request.status === 'DRAFT',
      statusLabel: PAYOUT_REQUEST_STATUS_LABELS[request.status as PayoutRequestStatus],
      totalHours,
      estimatedPayout,
      appliedHourlyRate,
      calculatedFinalPayout,
    };
  }

  private findNewestRateForPeriod(
    rates: PayoutRateUIModel[],
    periodStart: Date,
    periodEnd: Date,
  ): PayoutRateUIModel | null {
    const periodStartTime = periodStart.getTime();
    const periodEndTime = periodEnd.getTime();

    const overlappingRates = rates.filter((rate) => {
      // If valid_from is NULL, it applies backwards indefinitely (negative infinity)
      const validFrom = rate.valid_from ? new Date(rate.valid_from).getTime() : -Infinity;
      const validTo = rate.valid_to ? new Date(rate.valid_to).getTime() : Infinity;

      return validFrom <= periodEndTime && validTo > periodStartTime;
    });

    if (overlappingRates.length === 0) {
      return null;
    }

    const rateStartedInPeriodAndCurrent = overlappingRates.find((rate) => {
      // If valid_from is NULL, it started before the period
      if (!rate.valid_from) {
        return false;
      }
      const validFrom = new Date(rate.valid_from).getTime();
      const startedInPeriod = validFrom >= periodStartTime && validFrom <= periodEndTime;
      const isCurrent = rate.valid_to === null;

      return startedInPeriod && isCurrent;
    });

    if (rateStartedInPeriodAndCurrent) {
      return rateStartedInPeriodAndCurrent;
    }

    // Sort by valid_from, treating NULL as oldest (negative infinity)
    overlappingRates.sort((a, b) => {
      const aValidFrom = a.valid_from ? new Date(a.valid_from).getTime() : -Infinity;
      const bValidFrom = b.valid_from ? new Date(b.valid_from).getTime() : -Infinity;
      return bValidFrom - aValidFrom;
    });

    return overlappingRates[0];
  }

  private calculateTotalHours(hoursData: DailyHoursMap): number {
    return Object.values(hoursData).reduce((total, hours) => {
      return total + (typeof hours === 'number' ? hours : 0);
    }, 0);
  }

  private getCurrentPeriod(): string {
    return this.dateTimeService.__format(new Date(), 'yyyy-MM');
  }
}
